import { HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AppInitAuthManagerService,
    AuthActions,
    AuthState,
    ObjectState,
    StatefullValue,
    TokenModel,
    USER_INITIALIZATION_SERVICE,
} from '@meddev/fe-shared';
import { Store } from '@ngxs/store';
import { APP_CONSTANTS } from '@shared/core';
import {
    AuthServiceClientService,
    ErrorCodes,
    MCAAuthActions,
    MCAAuthState,
    RegistrationFieldModel,
    RegistrationRequestFieldDto,
} from '@shared/data';
import { EMPTY, Observable, catchError, filter, first, from, map, of, switchMap, tap } from 'rxjs';
import { AuthStateEnum } from '../../enum/auth/auth-state.enum';
import { AuthManagerBase } from '../../interface/auth-manager-base';
import { ChallengeParams } from '../../interface/auth/challenge-params';
import { LoginParams } from '../../interface/auth/login-params';
import { UserInfoModel } from '../../interface/auth/user-info-model';
import { UserStatusMapper } from '../../mapper/user-status-mapper';
@Injectable({
    providedIn: 'root',
})
export class AuthManagerPwdService implements AuthManagerBase {
    private readonly appInitAuthManagerService = inject(AppInitAuthManagerService);
    private readonly authServiceClientService = inject(AuthServiceClientService);
    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly appConstants = inject(APP_CONSTANTS);
    private readonly userStatusMapper = inject(UserStatusMapper);
    private readonly userInitService = inject(USER_INITIALIZATION_SERVICE);

    authState$ = this.store.select(AuthState.authState$);
    realm$ = this.store.select(AuthState.realm$);
    userStatus$ = this.store.select(MCAAuthState.userStatus$);
    chalengeParams$ = this.store.select(AuthState.challengeParams$);

    userInfoData$(): Observable<UserInfoModel | null | undefined> {
        return this.store.select(MCAAuthState.userInfo$);
    }

    handleHttpRequest(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return next.handle(request);
    }

    refreshToken(): Observable<TokenModel> {
        return this.appInitAuthManagerService.refreshToken();
    }

    logout() {
        this.appInitAuthManagerService.performLogout();
        return this.appInitAuthManagerService.onLogout$.pipe(
            first(),
            switchMap(() => this.authState$.pipe(first())),
        );
    }

    challenge(params: ChallengeParams) {
        return this.store.dispatch(new AuthActions.SetChallengeParams(params)).pipe(
            switchMap(() => this.store.dispatch(new AuthActions.SetAuthState({ state: ObjectState.VALID, value: AuthStateEnum.LOGIN }))),
            switchMap(() => this.authState$.pipe(first())),
        );
    }

    login({ username, password }: LoginParams) {
        if (!username || !password) {
            return this.store
                .dispatch(new AuthActions.SetAuthState({ state: ObjectState.ERROR, value: AuthStateEnum.LOGIN }))
                .pipe(switchMap(() => this.authState$.pipe(first())));
        }
        return this.appInitAuthManagerService.login(username, password).pipe(
            switchMap(response => {
                if ('challengeId' in response) {
                    return EMPTY;
                }
                const token: TokenModel = response as TokenModel;
                return this.userInitService.initializeUserData(token).pipe(
                    tap(() => this.appInitAuthManagerService.broadcastLogin(token)),
                    switchMap(() =>
                        this.store
                            .dispatch(new AuthActions.SetAuthState({ state: ObjectState.VALID, value: AuthStateEnum.HAS_ACCESS }))
                            .pipe(switchMap(() => this.onLoginSuccess$())),
                    ),
                );
            }),
            catchError(() =>
                this.store
                    .dispatch(new AuthActions.SetAuthState({ state: ObjectState.ERROR, value: AuthStateEnum.LOGIN }))
                    .pipe(switchMap(() => of(null))),
            ),
            switchMap(() => this.authState$.pipe(first())),
        );
    }

    refreshUserStatus(): Observable<unknown> {
        return this.userInfoData$().pipe(
            filter(userInfo => !!userInfo),
            switchMap(userInfo => {
                if (!userInfo?.id) {
                    return of(null);
                }
                return this.authServiceClientService.getUserStatus(userInfo.id).pipe(
                    map(userStatusDto => this.userStatusMapper.mapDtoToModel(userStatusDto)),
                    switchMap(userStatus => this.store.dispatch(new MCAAuthActions.SetUserStatus(userStatus)).pipe(map(() => userStatus))),
                    catchError(() => this.store.dispatch(new MCAAuthActions.SetUserStatus(null)).pipe(map(() => null))),
                );
            }),
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    register(_: RegistrationRequestFieldDto[]): Observable<StatefullValue<AuthStateEnum>> {
        return of({ value: AuthStateEnum.REGISTRATION, state: ObjectState.ERROR });
    }

    getRegistrationFields(): Observable<StatefullValue<RegistrationFieldModel[], ErrorCodes>> {
        return of({ value: [] as RegistrationFieldModel[], state: ObjectState.ERROR });
    }

    private onLoginSuccess$(): Observable<unknown> {
        const redirectUrl = this.route.snapshot.queryParams[this.appConstants.redirectAfterLoginParameter] || '/main';
        return from(this.router.navigateByUrl(redirectUrl));
    }
}
